import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Flex,
  Text,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import { FaCircleInfo, FaXmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import {
  white,
  textSecondary,
  primaryAlt,
  backgroundLight,
  newBorder,
  textPrimary,
  color2,
  color6,
  color5,
  color4,
  color3,
  buttonRedText,
} from "../../../utils/colors";
import useThemeColors from "../../../hooks/useThemeColors";
import useIsMobile from "../../../hooks/useIsMobile";

const WarningModal = ({
  open,
  setOpen,
  warningTitle,
  warningMessage,
  warningButtons,
  pendingStatus,
  confirmHandler,
  cancelHandler,
  statusBefore,
  statusAfter,
}) => {
  const { t } = useTranslation();
  const { textPrimaryMode } = useThemeColors();
  const isMobile = useIsMobile();

  const statusColors = {
    draft: backgroundLight,
    planning: color2,
    approved: color3,
    ongoing: color4,
    completed: color5,
    cancelled: color6,
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      isCentered
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius={{ base: "0", md: "1.5rem" }}
        w={{ base: "100%", md: "600px" }}
        minH={{ base: "100%", md: "auto" }}
        maxW="100vw"
        m={0}
        border={`1px solid ${newBorder}`}
      >
        {isMobile && (
          <ModalHeader
            p="1.5rem 1.5rem"
            borderBottom={`1px solid ${newBorder}`}
          >
            <Flex w="100%" align="center" justify="space-between">
              <Text fontSize="1rem" fontWeight="600" color={textPrimary}>
                {warningTitle}
              </Text>
            </Flex>
          </ModalHeader>
        )}

        <ModalBody p={["1.5rem 0rem 1.5rem", "1.5rem 1.5rem 1.5rem"]}>
          <IconButton
            position="absolute"
            top="1.5rem"
            right="1.5rem"
            size="sm"
            bg={backgroundLight}
            color={textSecondary}
            onClick={() => setOpen(false)}
            borderRadius="10rem"
            _hover={{ bg: primaryAlt, color: white }}
          >
            <Icon as={FaXmark} />
          </IconButton>
          <Flex
            w="100%"
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            p={[
              warningButtons ? "1rem 1.5rem 3rem" : "1rem 1.5rem 2rem",
              warningButtons ? "1rem rem 3rem" : "1rem 2rem 2rem",
            ]}
          >
            <Icon
              as={FaCircleInfo}
              fontSize="1.875rem"
              color={!warningButtons ? buttonRedText : primaryAlt}
              position="relative"
              top="-1px"
              m="0rem 0 0"
            />
            <Text
              fontSize={["1.5rem", "1.25rem"]}
              fontWeight="600"
              color={textPrimary}
              lineHeight="1.5"
              m="1rem auto 0"
              maxW={["100%", "90%"]}
            >
              {warningTitle}
            </Text>

            <Text
              fontSize={["md", "sm"]}
              fontWeight="500"
              color={textSecondary}
              m={warningButtons ? "0.5rem 0 0" : "0rem 0 0"}
              maxW={warningButtons ? "100%" : "80%"}
              lineHeight="1.8"
            >
              {warningButtons
                ? t("warnings.treatmentPlanStatusChangeText")
                : pendingStatus === "ongoing"
                ? t("warnings.approvedToOngoingStatusWarningText")
                : pendingStatus === "completed"
                ? t("warnings.completedAllStatusWarningText")
                : pendingStatus === "approved"
                ? t("warnings.patientSelectStatusWarningText")
                : pendingStatus === "cancelled"
                ? t("warnings.patientSelectStatusWarningText")
                : t("warnings.noServicesWarningText")}

              {warningButtons && (
                <>
                  <Text
                    as="span"
                    bg={statusColors[statusBefore]}
                    color={
                      statusColors[statusBefore] === color5
                        ? textPrimaryMode
                        : white
                    }
                    p="0.5rem 1rem"
                    borderRadius="0.75rem"
                    fontSize={["sm", "0.8rem"]}
                    ml={1}
                  >
                    {t(`treatmentPlanStatus.${statusBefore}`)}
                  </Text>
                  <Text as="span" mx={1}>
                    {t("common.to")}
                  </Text>
                  <Text
                    as="span"
                    bg={statusColors[statusAfter]}
                    color={
                      statusColors[statusAfter] === color5
                        ? textPrimaryMode
                        : white
                    }
                    p="0.5rem 1rem"
                    borderRadius="0.75rem"
                    fontSize={["sm", "0.8rem"]}
                  >
                    {t(`treatmentPlanStatus.${statusAfter}`)}
                  </Text>
                </>
              )}
            </Text>

            {warningButtons &&
              (pendingStatus === "cancelled" ||
                pendingStatus === "ongoing" ||
                pendingStatus === "planning" ||
                pendingStatus === "approved" ||
                pendingStatus === "completed") && (
                <Text
                  fontSize={["md", "0.8rem"]}
                  fontWeight="500"
                  color={buttonRedText}
                  maxW={["100%", "80%"]}
                  m="1rem auto 0"
                >
                  {warningMessage}
                </Text>
              )}
          </Flex>

          <Flex
            w="100%"
            align="center"
            justify="center"
            p={["0rem 1.5rem 0.5rem", "0rem 1.5rem 0.5rem"]}
            gap="1rem"
            borderTop={`0px solid ${newBorder}`}
          >
            <Button
              size={["lg", "md"]}
              fontSize="sm"
              borderRadius="10rem"
              bg={backgroundLight}
              color={textSecondary}
              fontWeight="500"
              p="1rem 1.5rem"
              w={["50%", "initial"]}
              onClick={cancelHandler}
              _hover={{ bg: primaryAlt, color: white }}
            >
              {warningButtons ? t("common.cancel") : t("button.back")}
            </Button>
            {warningButtons && (
              <Button
                size={["lg", "md"]}
                fontSize="sm"
                borderRadius="10rem"
                w={["50%", "initial"]}
                bg={textPrimary}
                color={white}
                p="1rem 1.5rem"
                onClick={confirmHandler}
                _hover={{ bg: primaryAlt, color: white }}
              >
                {t("treatmentPlan.confirmWarning")}
              </Button>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WarningModal;
